// RedApp (Vodafone) screen-pop.
//
// WHAT THIS DOES
// Per the Vodafone "Red App - Dialler Process Flow" spec: once a call gets
// CONNECTED, the dialer must open the RedApp Disposal screen for that customer:
//
//   https://www.redapp.vodafoneidea.com:1818/Disposal/Dialer?msisdn=<PrimaryNumber>
//     msisdn         (mandatory) primary mobile number
//     accountID      (optional)  FAID / CustCode
//     dialerUniqueID (optional)  dialer call unique id
//
// TAB REUSE, WITHOUT A POPUP PROMPT ON EVERY CALL
// RedApp must occupy ONE tab: if it is already open, every subsequent call
// reloads that same tab; only when no tab exists should a new one open.
//
// This is done by submitting a hidden GET form whose target is a fixed WINDOW
// NAME. The browser resolves that name to the existing RedApp tab and simply
// navigates it, which is not a popup and therefore never prompts.
//
// window.open() was tried first and rejected: called from a socket event it
// carries no user gesture, so Chrome treats every call as a popup and asks for
// permission again and again. The form approach is what the existing proven
// dialer integration uses.
//
// SAFETY / CONFIGURATION
// Everything is driven by the PROCESS settings (Tenant Admin > Process > RedApp),
// not by build-time env vars: the toggle and the URL are edited in the UI and
// take effect on the agent's next login, with no rebuild and no deployment.
// A process with the toggle off is completely unaffected.

// The window name that makes tab reuse work. Must stay stable across calls —
// changing it would start opening new tabs again.
const TAB_NAME = process.env.REACT_APP_REDAPP_TAB_NAME || 'CALLEX_REDAPP';

/**
 * Cleans a configured URL.
 *
 * A trailing "?" or "&" left over from copy-pasting breaks RedApp: the page is
 * requested with an empty query string and the server rejects it. Strip them so
 * whatever the admin pastes into the process settings works.
 *
 * @param {string} value Raw URL from the process settings.
 * @return {string} URL without trailing separators.
 */
const cleanUrl = (value) => String(value || '').trim().replace(/[?&]+$/, '');

/**
 * Builds the RedApp query values for one call.
 *
 * Every value is optional and omitted entirely when absent rather than sent
 * empty, which keeps the URL clean for RedApp's parser. accountID is the primary
 * identifier; the caller decides which one to pass (see redappPop).
 *
 * @param {Object} args Query values for this call.
 * @return {Object} Plain map of query name -> value.
 */
const buildParams = ({ msisdn, accountID, dialerUniqueID }) => {
  const params = {};
  if (accountID) params.accountID = String(accountID);
  if (msisdn) params.msisdn = String(msisdn);
  if (dialerUniqueID) params.dialerUniqueID = String(dialerUniqueID);

  return params;
};

/**
 * Navigates the named RedApp tab by submitting a hidden GET form at it.
 *
 * WHY A FORM AND NOT window.open
 * window.open() called from a socket event carries no user gesture, so Chrome
 * treats it as a popup and prompts for permission EVERY time. A form submission
 * aimed at a window name is a navigation, not a popup: when a tab with that
 * name already exists the browser simply navigates it, with no prompt at all.
 * This is the same mechanism the existing (proven) dialer integration uses.
 *
 * The query values go in as hidden inputs rather than in the action URL,
 * because a GET form discards any query string already present on the action.
 *
 * @param {string} baseUrl RedApp Disposal endpoint, from the process settings.
 * @param {Object} params Query name -> value map.
 * @return {void}
 */
const submitToNamedTab = (baseUrl, params) => {
  const form = document.createElement('form');
  form.method = 'GET';
  form.action = baseUrl;
  form.target = TAB_NAME;
  form.style.display = 'none';

  Object.keys(params).forEach((name) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = params[name];
    form.appendChild(input);
  });

  document.body.appendChild(form);
  try {
    form.submit();
  } finally {
    // Always clean up, even if submit throws, so the DOM never accumulates
    // orphaned forms across a shift's worth of calls.
    document.body.removeChild(form);
  }
};

/**
 * Navigates the named RedApp tab to a plain URL, with no query string at all.
 *
 * A GET form ALWAYS appends "?" even when it carries no inputs, which produces
 * a url RedApp rejects. An anchor with the same target reuses the tab exactly
 * like the form does, but navigates to the url untouched.
 *
 * @param {string} url Fully formed destination.
 * @return {void}
 */
const navigateNamedTab = (url) => {
  const link = document.createElement('a');
  link.href = url;
  link.target = TAB_NAME;
  link.style.display = 'none';

  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    document.body.removeChild(link);
  }
};

/**
 * Opens the RedApp Disposal screen for a connected call, reusing the existing
 * RedApp tab when one is already open.
 *
 * Never throws: a screen-pop failure must not break call handling, so every
 * problem is logged and reported through the return value instead.
 *
 * @param {Object} args
 * @param {string} args.url RedApp Disposal endpoint from the process settings.
 * @param {string} [args.msisdn] Primary mobile number. When absent the url is
 *        opened without any query string (fallback url case).
 * @param {string} [args.accountID] FAID / CustCode.
 * @param {string} [args.dialerUniqueID] Dialer call unique id.
 * @param {Function} [args.log] Optional logger, same shape as crmLanding's.
 * @return {boolean} true when the tab was opened or reloaded.
 */
export const openRedappTab = ({ url, msisdn, accountID, dialerUniqueID, log } = {}) => {
  const target = cleanUrl(url);

  if (!target) {
    log?.('RedApp screen-pop skipped: no RedApp URL configured on this process');
    return false;
  }

  try {
    // Both paths aim at the SAME window name, so the browser reuses the RedApp
    // tab when it exists and opens one when it does not — and neither asks for
    // popup permission the way window.open does.
    if (accountID || msisdn) {
      submitToNamedTab(target, buildParams({ msisdn, accountID, dialerUniqueID }));
    } else {
      // Fallback url: no identifier, so no query string — a form (which would
      // append "?") is the wrong tool.
      navigateNamedTab(target);
    }

    log?.('RedApp tab opened/reloaded');
    return true;
  } catch (error) {
    log?.(`RedApp screen-pop failed: ${String(error)}`);
    return false;
  }
};

